import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { Json } from '@/integrations/supabase/types';

export interface CommunityDiscussionsPaginatedOptions {
  pageSize?: number;
  category?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  searchQuery?: string;
  enabled?: boolean;
}

export interface CommunityDiscussionPaginated {
  discussion_id: string;
  discussion_title: string;
  discussion_content: string;
  discussion_category: string;
  discussion_created_at: string;
  discussion_updated_at: string;
  discussion_likes: number;
  discussion_replies: number;
  discussion_views: number;
  discussion_is_hot: boolean;
  discussion_user_id: string;
  discussion_openai_thread_id: string;
  discussion_author_id: string;
  discussion_author_first_name: string;
  discussion_author_profile_picture_url: string;
  discussion_is_liked_by_user: boolean;
  total_count: number;
  has_more_pages: boolean;
  current_page: number;
  total_pages: number;
  category_counts: Json;
}

export interface CommunityDiscussionsPage {
  discussions: CommunityDiscussionPaginated[];
  pagination: {
    totalCount: number;
    hasMorePages: boolean;
    currentPage: number;
    totalPages: number;
  };
  categoryCounts: Record<string, number>;
  nextOffset?: number;
}

// Create stable query key that excludes undefined values
const createQueryKey = (options: CommunityDiscussionsPaginatedOptions, userId?: string) => {
  const keyParts: (string | number | boolean)[] = ['community-discussions-paginated'];
  
  if (userId) keyParts.push('user', userId);
  if (options.category) keyParts.push('category', options.category);
  if (options.sortBy && options.sortBy !== 'created_at') keyParts.push('sort', options.sortBy);
  if (options.sortOrder && options.sortOrder !== 'desc') keyParts.push('order', options.sortOrder);
  if (options.searchQuery) keyParts.push('search', options.searchQuery);
  if (options.pageSize && options.pageSize !== 20) keyParts.push('size', options.pageSize);
  
  return keyParts;
};

export const useCommunityDiscussionsPaginated = (options: CommunityDiscussionsPaginatedOptions = {}) => {
  const { user } = useAuth();
  const {
    pageSize = 20,
    category = null,
    sortBy = 'created_at',
    sortOrder = 'desc',
    searchQuery = null,
    enabled = true
  } = options;

  return useInfiniteQuery({
    queryKey: createQueryKey(options, user?.id),
    queryFn: async ({ pageParam = 0 }) => {
      const { data, error } = await supabase.rpc('get_community_discussions_paginated', {
        p_user_id: user?.id || null,
        p_limit: pageSize,
        p_offset: pageParam,
        p_category: category,
        p_sort_by: sortBy,
        p_sort_order: sortOrder,
        p_search_query: searchQuery
      });

      if (error) {
        console.error('RPC Error:', error);
        console.error('RPC Parameters:', {
          p_user_id: user?.id || null,
          p_limit: pageSize,
          p_offset: pageParam,
          p_category: category,
          p_sort_by: sortBy,
          p_sort_order: sortOrder,
          p_search_query: searchQuery
        });
        throw error;
      }
      if (!data || !Array.isArray(data) || data.length === 0) {
        return {
          discussions: [],
          pagination: {
            totalCount: 0,
            hasMorePages: false,
            currentPage: 0,
            totalPages: 0
          },
          categoryCounts: {},
          nextOffset: undefined
        };
      }

      // Transform the flattened data
      const discussions = data.map(row => ({
        discussion_id: row.discussion_id,
        discussion_title: row.discussion_title,
        discussion_content: row.discussion_content,
        discussion_category: row.discussion_category,
        discussion_created_at: row.discussion_created_at,
        discussion_updated_at: row.discussion_updated_at,
        discussion_likes: row.discussion_likes,
        discussion_replies: row.discussion_replies,
        discussion_views: row.discussion_views,
        discussion_is_hot: row.discussion_is_hot,
        discussion_user_id: row.discussion_user_id,
        discussion_openai_thread_id: row.discussion_openai_thread_id,
        discussion_author_id: row.discussion_author_id,
        discussion_author_first_name: row.discussion_author_first_name,
        discussion_author_profile_picture_url: row.discussion_author_profile_picture_url,
        discussion_is_liked_by_user: row.discussion_is_liked_by_user,
        total_count: row.total_count,
        has_more_pages: row.has_more_pages,
        current_page: row.current_page,
        total_pages: row.total_pages,
        category_counts: row.category_counts || {}
      }));

      // Get pagination info from first row (same for all rows)
      const firstRow = data[0];
      const pagination = {
        totalCount: Number(firstRow.total_count),
        hasMorePages: firstRow.has_more_pages,
        currentPage: firstRow.current_page,
        totalPages: firstRow.total_pages
      };

      const categoryCounts = firstRow.category_counts || {};
      const nextOffset = firstRow.has_more_pages ? pageParam + pageSize : undefined;

      return {
        discussions,
        pagination,
        categoryCounts,
        nextOffset
      } as CommunityDiscussionsPage;
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    initialPageParam: 0,
    enabled: enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('not authenticated')) {
        return false;
      }
      
      // Don't retry on function not found errors
      if (error?.code === '42883' || error?.message?.includes('function') || error?.message?.includes('does not exist')) {
        return false;
      }
      
      // Retry network errors up to 2 times
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Helper hook to get flattened discussions from all pages
export const useFlattenedCommunityDiscussions = (options: CommunityDiscussionsPaginatedOptions = {}) => {
  const query = useCommunityDiscussionsPaginated(options);
  
  const discussions = query.data?.pages.flatMap(page => page.discussions) || [];
  const pagination = query.data?.pages[0]?.pagination;
  const categoryCounts = query.data?.pages[0]?.categoryCounts || {};
  
  return {
    ...query,
    discussions,
    pagination,
    categoryCounts,
    totalCount: pagination?.totalCount || 0,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage
  };
};

// Simplified hook that matches the existing useCommunityDiscussions interface
export const useCommunityDiscussionsInfinite = (options: CommunityDiscussionsPaginatedOptions = {}) => {
  const { discussions, categoryCounts, ...rest } = useFlattenedCommunityDiscussions(options);
  
  // Transform to match existing interface
  const transformedDiscussions = discussions.map(discussion => ({
    id: discussion.discussion_id,
    title: discussion.discussion_title,
    content: discussion.discussion_content,
    category: discussion.discussion_category,
    created_at: discussion.discussion_created_at,
    updated_at: discussion.discussion_updated_at,
    likes: discussion.discussion_likes, // Now uses real-time calculation from database
    replies: discussion.discussion_replies,
    views: discussion.discussion_views,
    is_hot: discussion.discussion_is_hot,
    user_id: discussion.discussion_user_id,
    openai_thread_id: discussion.discussion_openai_thread_id,
    is_liked_by_user: discussion.discussion_is_liked_by_user,
    users: {
      id: discussion.discussion_author_id,
      first_name: discussion.discussion_author_first_name,
      profile_picture_url: discussion.discussion_author_profile_picture_url
    }
  }));
  
  return {
    ...rest,
    data: transformedDiscussions,
    categoryCounts
  };
}; 